// VDO Ninja の push UI を iframe で表示するコンポーネント(設計書 §5.4.2 推奨は
// 直接バンドルだが、Phase 5 Step 2 では iframe で最短実装)。
//
// allow="camera; microphone; autoplay" を付けることで、別 origin の iframe
// からでも getUserMedia が呼べる(Permission Policy)。
//
// 2026-05-07: iframe 内 publisher self-view の orientation 自動補正を抑止する
// CSS / JS を注入する機能を追加。同 origin の iframe (= staging で
// CloudFront /vdo/* 経由) であれば contentDocument にアクセスできるため、
// VDO Ninja の <video> 要素に対して transform: none !important を当て、
// さらに screen.orientation の角度に対して逆回転をかけて iOS Safari の
// 自動回転を打ち消す。これで配信者の自己 view が「mosaic CLI / FC2 ライブ
// が実際に受け取る向き(= センサー native、横持ち時は sideways)」と一致し、
// 横持ちで誤って配信し続ける事故を防げる。
//
// 別 origin (dev: localhost:8444 と localhost:5173 は別 origin) の場合は
// SecurityError で contentDocument にアクセスできず、try/catch で握り潰す。

import { useEffect, useRef } from 'react';

interface VdoPushFrameProps {
  src: string;
  height?: number | string;
  title?: string;
}

// VDO Ninja iframe 内に注入する orientation override スクリプト。
//
// 何を達成するか:
//   publisher 自己 view を「mosaic CLI / FC2 ライブが受け取る向き
//   (= track の生 frame をそのまま表示)」と一致させ、配信者が横持ちで
//   誤って配信し続ける事故を防ぐ(WYSIWYG)。
//
// iOS Safari の挙動と判定ロジック(2026-05-07 検証で確定):
//   - getUserMedia は **配信開始時** の screen orientation に応じて track
//     の出力寸法を決める:
//       縦持ちで開始 → track 608×1080 (portrait)
//       横持ちで開始 → track 1080×608 (landscape)
//   - track と screen orientation が一致している間は iOS の auto-rotation
//     は発動しない(`<video>` は track をそのまま表示)。
//   - track と screen orientation が乖離した時(配信中に物理回転した時)に
//     のみ iOS Safari が `<video>` 要素に CSS 自動回転を適用する。
//   - したがって counter-rotation は両者が乖離している時 **だけ** 適用する。
//     一致時に -angle を当てると過剰回転になる(2026-05-07 報告事象)。
//
// 戦略:
//   1. 全 <video> 要素に transform: none !important を当てる(VDO Ninja が
//      設定する scaleX 系の transform を打ち消す土台)。
//   2. track 寸法 (`video.videoWidth/videoHeight`) と screen.orientation.angle
//      を比較。
//        - 一致(track=portrait & angle in {0,180} / track=landscape & angle in
//          {90,270}): iOS は回転していない → counter-rotation 不要
//        - 乖離: iOS が +angle で auto-rotate → -angle で打ち消し
//   3. orientationchange / video の loadedmetadata イベントで再評価。
const ORIENTATION_OVERRIDE_SCRIPT = `
(function() {
  var STYLE_ID = 'safelens-publisher-orientation-override';
  var existing = document.getElementById(STYLE_ID);
  if (existing) existing.remove();

  // Step 1: ベースの CSS。VDO Ninja の inline style.transform を !important で勝つ。
  var baseStyle = document.createElement('style');
  baseStyle.id = STYLE_ID;
  baseStyle.textContent =
    'video, video.myVideo, #videosource, #previewWebcam, ' +
    '#videosource video, #previewWebcam video { ' +
    '  transform: none !important; ' +
    '  -webkit-transform: none !important; ' +
    '}';
  document.head.appendChild(baseStyle);

  // Step 2: 動的 counter-rotation を別 <style> に出して角度変更を反映。
  var dynStyle = document.createElement('style');
  dynStyle.id = STYLE_ID + '-dynamic';
  document.head.appendChild(dynStyle);

  function findTrackVideo() {
    // VDO Ninja は session.videoElement で publisher の自己 view <video> を
    // 持つことがある。ない場合は DOM 全体から videoWidth が読める <video> を探す。
    try {
      if (window.session && window.session.videoElement &&
          window.session.videoElement.videoWidth) {
        return window.session.videoElement;
      }
    } catch (e) {}
    var videos = document.querySelectorAll('video');
    for (var i = 0; i < videos.length; i++) {
      var v = videos[i];
      if (v.videoWidth && v.videoHeight) {
        return v;
      }
    }
    return null;
  }

  function getScreenAngle() {
    if (screen && screen.orientation && typeof screen.orientation.angle === 'number') {
      return screen.orientation.angle;
    }
    if (typeof window.orientation === 'number') {
      return window.orientation;
    }
    return 0;
  }

  function applyCounterRotation() {
    var angle = getScreenAngle();
    var v = findTrackVideo();

    if (!v) {
      // track 未準備 — メタデータ読込後に再試行
      dynStyle.textContent = '';
      return;
    }

    var trackPortrait = v.videoWidth < v.videoHeight;
    var screenPortrait = (angle === 0 || angle === 180);

    // 一致: iOS は auto-rotation していない → counter 不要
    // 乖離: iOS が +angle で auto-rotate → -angle で打ち消し
    if (trackPortrait === screenPortrait) {
      dynStyle.textContent = '';
      return;
    }

    dynStyle.textContent =
      'video, video.myVideo, #videosource, #previewWebcam, ' +
      '#videosource video, #previewWebcam video { ' +
      '  transform: rotate(' + (-angle) + 'deg) !important; ' +
      '  -webkit-transform: rotate(' + (-angle) + 'deg) !important; ' +
      '  transform-origin: center center !important; ' +
      '}';
  }

  function scheduleApply() {
    // iOS は orientationchange / loadedmetadata の発火と DOM/track 反映に
    // ラグがあるので何回か遅延して呼ぶ(過剰でも害はない)。
    setTimeout(applyCounterRotation, 0);
    setTimeout(applyCounterRotation, 150);
    setTimeout(applyCounterRotation, 500);
    setTimeout(applyCounterRotation, 1500);
  }

  scheduleApply();

  window.addEventListener('orientationchange', scheduleApply);
  if (screen && screen.orientation && screen.orientation.addEventListener) {
    screen.orientation.addEventListener('change', scheduleApply);
  }

  // <video> の loadedmetadata でも再判定(track 寸法が確定するタイミング)。
  function attachLoadedMetadata() {
    var videos = document.querySelectorAll('video');
    videos.forEach(function(v) {
      if (v.dataset && v.dataset.safelensOrientationListener) return;
      if (v.dataset) v.dataset.safelensOrientationListener = '1';
      v.addEventListener('loadedmetadata', scheduleApply);
      v.addEventListener('resize', scheduleApply);
    });
  }
  attachLoadedMetadata();

  // <video> が後から DOM に追加されるケースに備え MutationObserver を貼る。
  try {
    var mo = new MutationObserver(function() {
      attachLoadedMetadata();
      // 念のため再評価
      setTimeout(applyCounterRotation, 100);
    });
    mo.observe(document.body, { childList: true, subtree: true });
  } catch (e) {}
})();
`;

export function VdoPushFrame({ src, height = 480, title = 'VDO Ninja push' }: VdoPushFrameProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const injectOverride = () => {
      let doc: Document | null = null;
      try {
        // 別 origin (dev) の iframe にアクセスすると SecurityError。
        // staging (same-origin via CloudFront /vdo/*) では成功する。
        doc = iframe.contentDocument;
      } catch {
        return;
      }
      if (!doc) return;

      try {
        // CSS / JS の二重注入を避ける(既に同 id の <style> があれば skip)。
        if (doc.getElementById('safelens-publisher-orientation-override')) {
          return;
        }
        // <script> 経由でクロージャ実行。`<script>` を innerHTML で挿入しても
        // 実行されないため、createElement → textContent → appendChild する。
        const script = doc.createElement('script');
        script.textContent = ORIENTATION_OVERRIDE_SCRIPT;
        doc.head.appendChild(script);
      } catch (e) {
        if (typeof console !== 'undefined') {
          // eslint-disable-next-line no-console
          console.warn('VdoPushFrame: orientation override injection failed', e);
        }
      }
    };

    iframe.addEventListener('load', injectOverride);

    // すでにロード完了している場合に備えて即時実行も試す(StrictMode 二重 effect 等)。
    try {
      const readyState = iframe.contentDocument?.readyState;
      if (readyState === 'complete' || readyState === 'interactive') {
        injectOverride();
      }
    } catch {
      // cross-origin: 無視
    }

    return () => {
      iframe.removeEventListener('load', injectOverride);
    };
  }, [src]);

  return (
    <div
      style={{
        width: '100%',
        height,
        background: '#000',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
        border: 'var(--border-default)',
      }}
    >
      <iframe
        ref={iframeRef}
        src={src}
        title={title}
        allow="camera; microphone; autoplay; display-capture; fullscreen"
        // sandbox は付けない: VDO Ninja は同 origin (iframe 内) で
        // localStorage / WebSocket / RTCPeerConnection を要求するため
        // Referrer Policy: VDO Ninja への遷移時、portal の URL(=パフォーマー
        // 識別に繋がる可能性)を Referer ヘッダで Leak しないよう no-referrer。
        referrerPolicy="no-referrer"
        style={{
          width: '100%',
          height: '100%',
          border: 0,
          background: '#000',
        }}
      />
    </div>
  );
}
